// TES-1961 (PT-TSK-Consumer-UI, Option 1) — config-getriebenes Status/Fortschritt-Widget der verknüpften
// PT-TSK-Task-Gruppe am Mangel (read-only Consumer; §6b „Status/Fortschritt der verlinkten Task-Gruppen").
// Reuse: EntityChip (@plasser/ui) + Task-Type-Config — kein Eigenbau-Chip. Rendert nur den gespiegelten
// Status (defect_task_links.pt_task_status); CHK ändert PT-TSK-Status NICHT (daher Anzeige, keine Transition).
import { useTranslation } from "react-i18next";
import { CheckCircle2 } from "lucide-react";
import { EntityChip } from "@plasser/ui";
import { computeTaskGroupProgress, resolveStatusConfig } from "./taskTypeConfig";
import type { DefectTaskLink } from "@/hooks/useDefectTaskLinks";

interface Props {
  links: DefectTaskLink[];
  isLoading?: boolean;
}

/** Voll-Widget: Fortschrittsbalken (erledigt/total) + ein EntityChip je Task (Status-getönt). */
export function TaskGroupProgress({ links, isLoading = false }: Props) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        data-testid="task-group-progress-loading"
        className="m-4 h-12 animate-pulse rounded bg-muted"
        aria-hidden="true"
      />
    );
  }

  const progress = computeTaskGroupProgress(links.map((l) => l.pt_task_status));
  // Leeren Zustand übernimmt die umgebende Karte (eigener Empty-State) — hier nichts rendern.
  if (progress.total === 0) return null;

  const label = t("defects.taskLinks.progress.label");
  return (
    <div data-testid="task-group-progress" className="mx-4 mt-4 space-y-2">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-muted-foreground">{label}</span>
        <span
          data-testid="task-group-progress-count"
          className="inline-flex items-center gap-1 font-medium"
        >
          {progress.allDone && <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />}
          {t("defects.taskLinks.progress.count", { done: progress.done, total: progress.total })}
        </span>
      </div>

      <ProgressBar percent={progress.percent} allDone={progress.allDone} label={label} />
      <TaskChips links={links} />
    </div>
  );
}

/** Reiner Balken — Tönung config-/Fortschritt-abhängig (ausgelagert, hält das Haupt-Widget schlank). */
function ProgressBar({
  percent,
  allDone,
  label,
}: {
  percent: number;
  allDone: boolean;
  label: string;
}) {
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-2 w-full overflow-hidden rounded-full bg-muted"
    >
      <div
        data-testid="task-group-progress-bar"
        className={allDone ? "h-full bg-success" : "h-full bg-primary"}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

/** Ein EntityChip je Task, getönt über die Task-Type-Config (kein Eigenbau-Chip). */
function TaskChips({ links }: { links: DefectTaskLink[] }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1" data-testid="task-group-progress-chips">
      {links.map((l) => {
        const cfg = resolveStatusConfig(l.pt_task_status);
        return (
          <EntityChip
            key={l.id}
            label={`${l.pt_task_id} · ${t(cfg.labelKey)}`}
            tone={cfg.tone}
            data-testid={`task-chip-${l.id}`}
          />
        );
      })}
    </div>
  );
}
